import { Link } from '@tanstack/react-router';
import type { ReactNode } from 'react';
import { ENVIRONMENT_LABELS } from '@visualitics-toolkit/shared';

import { Badge, Button, EnvDot } from '../atoms';
import { ErrorBlock, LoadingBlock, formatDateTime } from '../molecules';
import { useActivateConnection, useConnections, useSession } from '../../lib/queries';
import { Dialog } from './Dialog';

export function ConnectionSwitcher({ onClose }: { onClose: () => void }): ReactNode {
  const connections = useConnections();
  const session = useSession();
  const activate = useActivateConnection();

  return (
    <Dialog title="Trocar conexão" onClose={onClose} wide>
      {connections.isLoading && <LoadingBlock />}
      {activate.error instanceof Error && <ErrorBlock message={activate.error.message} />}

      {connections.data?.length === 0 && (
        <p className="text-sm text-muted">
          Nenhuma conexão cadastrada.{' '}
          <Link to="/settings" onClick={onClose} className="text-accent underline">
            Cadastrar agora
          </Link>
          .
        </p>
      )}

      <ul className="space-y-2">
        {connections.data?.map((connection) => {
          const active = connection.id === session.data?.activeConnectionId;
          return (
            <li
              key={connection.id}
              data-env={connection.environment}
              className="flex items-center justify-between gap-3 rounded-md border border-border px-3 py-2"
            >
              <div className="min-w-0">
                <p className="flex items-center gap-2 text-sm font-medium text-content">
                  <EnvDot environment={connection.environment} />
                  {connection.name}
                  <Badge>{ENVIRONMENT_LABELS[connection.environment]}</Badge>
                  {active && <Badge tone="accent">ativa</Badge>}
                </p>
                <p className="truncate font-mono text-xs text-muted">
                  {connection.host}:{connection.port}/{connection.database} · último teste:{' '}
                  {formatDateTime(connection.lastTestedAt)}
                </p>
              </div>
              <Button
                variant={active ? 'ghost' : 'primary'}
                disabled={active}
                loading={activate.isPending}
                onClick={() => activate.mutate(connection.id, { onSuccess: onClose })}
              >
                {active ? 'em uso' : 'ativar'}
              </Button>
            </li>
          );
        })}
      </ul>
    </Dialog>
  );
}
